import { useRef, useState } from 'react';
import { Editor } from '@revolist/react-datagrid';
import type { ColumnRegular, DataType, EditorCtr, EditorType } from '@revolist/react-datagrid';
import {
  Autocomplete,
  Box,
  ListSubheader,
  MenuItem,
  Select,
  TextField,
  ThemeProvider,
  createTheme,
} from '@mui/material';
import type { Theme } from '@mui/material';

export type SelectOption = string | { label: string; value: string; group?: string };

export const toSelectOption = (option: SelectOption) =>
  typeof option === 'string' ? { label: option, value: option } : option;

/** Label to show for a stored value — the value itself when it is not in the list. */
export const getSelectOptionLabel = (options: readonly SelectOption[], value: unknown) => {
  const stored = (value ?? '') as string;
  return options.map(toSelectOption).find((option) => option.value === stored)?.label ?? stored;
};

/**
 * A fixed option list, or a function deriving one from the row being edited —
 * e.g. Raw Unit's list depends on the row's own Tag Type. An empty resolved
 * list doubles as "nothing to offer yet", which both the editor and the
 * resting-state cell template treat as disabled.
 */
export type SelectOptionsSource<TModel extends DataType = DataType> =
  | readonly SelectOption[]
  | ((model: TModel) => readonly SelectOption[]);

export const resolveSelectOptions = <TModel extends DataType = DataType>(
  source: SelectOptionsSource<TModel>,
  model: TModel
): readonly SelectOption[] => (typeof source === 'function' ? source(model) : source);

let sharedTheme: Theme | null = null;
const fallbackTheme = createTheme();

export const setEditorTheme = (theme: Theme) => {
  sharedTheme = theme;
};

/** Config for a select column's editor — a plain dropdown by default, or a
 * type-to-filter autocomplete if `autocomplete: true`. */
export type SelectEditorConfig = { autocomplete?: boolean };

type SelectEditorProps = EditorType & { options: SelectOptionsSource } & SelectEditorConfig;

export const FIELD_HEIGHT = 37;
/** RHFSelect's menu: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP */
const MENU_MAX_HEIGHT = 48 * 4.5 + 8;

const SelectCellEditor = ({
  column,
  val,
  save,
  close,
  options,
  autocomplete,
}: SelectEditorProps) => {
  const initial = (val ?? column?.value ?? '') as string;
  const [open, setOpen] = useState(true);

  const savedRef = useRef(false);
  const items = resolveSelectOptions(options, column?.model).map(toSelectOption);

  if (autocomplete) {
    return (
      <ThemeProvider theme={sharedTheme ?? fallbackTheme}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            height: '100%',
            width: '100%',
            px: 2,
            boxSizing: 'border-box',
          }}
        >
          <Autocomplete
            open={open}
            fullWidth
            size="small"
            // The blank option isn't listed: the clear (x) button commits it
            options={items.filter((item) => item.value !== '')}
            getOptionLabel={(item) => item.label}
            isOptionEqualToValue={(item, optionValue) => item.value === optionValue.value}
            value={items.find((item) => item.value === initial) ?? null}
            onChange={(_event, newValue, reason) => {
              // Typing/clearing only filters the list — nothing commits
              // unless an option is actually picked; any
              // other exit (click away, Escape) discards it below.
              if (reason !== 'selectOption') return;
              savedRef.current = true;
              save(newValue?.value ?? '');
            }}
            onClose={() => {
              setOpen(false);
              if (!savedRef.current) {
                close();
              }
            }}
            renderInput={(params) => <TextField {...params} autoFocus />}
            ListboxProps={{ style: { maxHeight: MENU_MAX_HEIGHT } }}
            componentsProps={{
              // The x commits the blank option
              clearIndicator: {
                onClick: () => {
                  savedRef.current = true;
                  save('');
                },
              },
              paper: {
                sx: {
                  fontSize: 'inherit',
                  backgroundColor: (sharedTheme ?? fallbackTheme).palette.background.default,
                },
              },
            }}
            sx={{
              width: '100%',
              '& .MuiOutlinedInput-root': {
                fontSize: (theme) => theme.typography.body2.fontSize,
                backgroundColor: (theme) => theme.palette.background.default,
              },
            }}
          />
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={sharedTheme ?? fallbackTheme}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          height: '100%',
          width: '100%',
          px: 2,
          boxSizing: 'border-box',
        }}
      >
        <Select
          open={open}
          autoFocus
          fullWidth
          size="small"
          value={items.some((item) => item.value === initial) ? initial : ''}
          onChange={(event) => {
            savedRef.current = true;
            save(event.target.value);
          }}
          onClose={() => {
            setOpen(false);
            if (!savedRef.current) {
              close();
            }
          }}
          sx={{
            height: FIELD_HEIGHT,
            fontSize: (theme) => theme.typography.body2.fontSize,
            backgroundColor: (theme) => theme.palette.background.default,
            '& .MuiSelect-select': {
              display: 'flex',
              alignItems: 'center',
              minHeight: 'unset',
              py: 0,
            },
          }}
          MenuProps={{
            PaperProps: {
              style: {
                maxHeight: MENU_MAX_HEIGHT,
                backgroundColor: (sharedTheme ?? fallbackTheme).palette.background.default,
              },
            },
          }}
        >
          {items.flatMap((item, index) => {
            const startsGroup = !!item.group && item.group !== items[index - 1]?.group;

            return [
              ...(startsGroup
                ? [
                    <ListSubheader key={`group-${item.group}`} sx={{ fontSize: 'inherit' }}>
                      {item.group}
                    </ListSubheader>,
                  ]
                : []),
              <MenuItem key={item.value} value={item.value} sx={{ fontSize: 'inherit' }}>
                {item.label}
              </MenuItem>,
            ];
          })}
        </Select>
      </Box>
    </ThemeProvider>
  );
};

/**
 * Builds a RevoGrid editor constructor bound to an option list — fixed, or
 * resolved per row. Call once per column at module scope — `columns` has to
 * stay a stable reference.
 *
 * Stores the source itself (not a resolved list), fixed array or per-row
 * function alike — range-edit sanitization needs to resolve a dependent
 * column's options against each row it's validating, not one list shared by
 * every row.
 */
const editorOptions = new WeakMap<EditorCtr, SelectOptionsSource>();

export const getEditorOptions = (
  editor: ColumnRegular['editor']
): SelectOptionsSource | undefined =>
  typeof editor === 'string' || !editor ? undefined : editorOptions.get(editor);

/**
 * Builds a RevoGrid editor constructor bound to an option list: fixed, or
 * resolved per row for dependent cells (raw unit depends on tag type for instance)
 */
export const createSelectEditor = (options: SelectOptionsSource, config?: SelectEditorConfig) => {
  const editor = Editor((props: EditorType) => (
    <SelectCellEditor {...props} options={options} autocomplete={config?.autocomplete} />
  ));
  editorOptions.set(editor, options);
  return editor;
};
